<template>
  <div class="stock-dashboard">
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

    <v-alert v-if="loadError" type="error" text dense>
      {{ $t("DASHBOARD.LOAD_ERROR") }}
    </v-alert>

    <v-alert v-else-if="!loading && !hasContent" type="info" text dense>
      {{ $t("DASHBOARD.EMPTY") }}
    </v-alert>

    <template v-if="hasContent">
      <v-row dense class="mb-2">
        <v-col v-if="inventory" cols="12" sm="6" lg="4">
          <v-card outlined class="stock-dashboard-card stock-dashboard-card--inventory" @click="open('stock-inventory')">
            <v-card-text>
              <div class="caption grey--text text--darken-1">{{ $t("DASHBOARD.INVENTORY") }}</div>
              <div class="text-h5 font-weight-bold">{{ inventoryHeadline }}</div>
              <div class="caption grey--text text--darken-1 mb-3">
                {{ formatQty(inventory.quantity_on_hand) }}
                · {{ $t("DASHBOARD.PRODUCTS", { count: inventory.product_count }) }}
              </div>
              <div v-if="compositionTotal > 0" class="stock-dashboard-bar mb-2">
                <span class="stock-dashboard-bar__free" :style="{ width: segmentWidth(inventory.on_hand_free_quantity) }"></span>
                <span class="stock-dashboard-bar__reserved" :style="{ width: segmentWidth(inventory.reserved_quantity) }"></span>
                <span class="stock-dashboard-bar__blocked" :style="{ width: segmentWidth(inventory.blocked_quantity) }"></span>
              </div>
              <div class="d-flex flex-wrap">
                <span class="stock-dashboard-legend stock-dashboard-legend--free">{{ $t("DASHBOARD.FREE") }} {{ formatQty(inventory.on_hand_free_quantity) }}</span>
                <span class="stock-dashboard-legend stock-dashboard-legend--reserved">{{ $t("DASHBOARD.RESERVED") }} {{ formatQty(inventory.reserved_quantity) }}</span>
                <span class="stock-dashboard-legend stock-dashboard-legend--blocked">{{ $t("DASHBOARD.BLOCKED") }} {{ formatQty(inventory.blocked_quantity) }}</span>
              </div>
              <div class="caption grey--text text--darken-1 mt-2">
                {{ $t("DASHBOARD.EXPECTED") }} {{ formatQty(inventory.expected_quantity) }}
                · {{ $t("DASHBOARD.PROJECTED") }} {{ formatQty(inventory.projected_quantity) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="purchaseOrders" cols="12" sm="6" lg="4">
          <v-card outlined class="stock-dashboard-card stock-dashboard-card--po" @click="open('stock-purchase-orders')">
            <v-card-text>
              <div class="caption grey--text text--darken-1">{{ $t("DASHBOARD.OPEN_PO") }}</div>
              <div class="text-h5 font-weight-bold">{{ purchaseOrders.open_count }}</div>
              <div v-for="(line, index) in moneyLines(purchaseOrders.totals_by_currency)" :key="'po-' + index" class="caption">
                {{ line }}
              </div>
              <div class="caption grey--text text--darken-1 mt-2">
                {{ $t("DASHBOARD.DRAFTS", { count: purchaseOrders.draft_count }) }}
                <span v-if="purchaseOrders.overdue_count" class="error--text">
                  · {{ $t("DASHBOARD.OVERDUE", { count: purchaseOrders.overdue_count }) }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="salesOrders" cols="12" sm="6" lg="4">
          <v-card outlined class="stock-dashboard-card stock-dashboard-card--so" @click="open('stock-sales-orders')">
            <v-card-text>
              <div class="caption grey--text text--darken-1">{{ $t("DASHBOARD.OPEN_SO") }}</div>
              <div class="text-h5 font-weight-bold">{{ salesOrders.open_count }}</div>
              <div v-for="(line, index) in moneyLines(salesOrders.totals_by_currency)" :key="'so-' + index" class="caption">
                {{ line }}
              </div>
              <div class="caption grey--text text--darken-1 mt-2">
                {{ $t("DASHBOARD.DRAFTS", { count: salesOrders.draft_count }) }}
                <span v-if="salesOrders.overdue_count" class="error--text">
                  · {{ $t("DASHBOARD.OVERDUE", { count: salesOrders.overdue_count }) }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="deals" cols="12" sm="6" lg="4">
          <v-card outlined class="stock-dashboard-card stock-dashboard-card--deals" @click="open('deals-list')">
            <v-card-text>
              <div class="caption grey--text text--darken-1">{{ $t("DASHBOARD.OPEN_DEALS") }}</div>
              <div class="text-h5 font-weight-bold">{{ deals.open_count }}</div>
              <div class="caption grey--text text--darken-1 mt-2">
                {{ $t("DASHBOARD.BUYING") }} {{ deals.buying_count }}
                · {{ $t("DASHBOARD.SELLING") }} {{ deals.selling_count }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="transfers" cols="12" sm="6" lg="4">
          <v-card outlined class="stock-dashboard-card stock-dashboard-card--transfers" @click="open('stock-transfer-orders')">
            <v-card-text>
              <div class="caption grey--text text--darken-1">{{ $t("DASHBOARD.TRANSFERS") }}</div>
              <div class="text-h5 font-weight-bold">{{ transfers.total }}</div>
              <div class="caption grey--text text--darken-1 mt-2">
                {{ $t("DASHBOARD.TO_SHIP") }} {{ transfers.to_ship }}
                · {{ $t("DASHBOARD.TO_RECEIVE") }} {{ transfers.to_receive }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col v-if="purchaseOrders" cols="12" lg="4">
          <v-card outlined class="stock-dashboard-list">
            <v-card-title class="subtitle-1 py-3">
              {{ $t("DASHBOARD.NEXT_ARRIVALS") }}
              <v-spacer></v-spacer>
              <v-btn text small color="primary" @click="open('stock-purchase-orders')">{{ $t("DASHBOARD.VIEW_ALL") }}</v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <div v-if="!purchaseOrders.upcoming.length" class="pa-4 caption grey--text">{{ $t("DASHBOARD.EMPTY_LIST") }}</div>
            <div
              v-for="row in purchaseOrders.upcoming"
              :key="'po-row-' + row.id"
              class="stock-dashboard-row"
              @click="open('stock-purchase-orders-view', row.id)"
            >
              <div class="d-flex justify-space-between align-start">
                <strong>{{ row.code }}</strong>
                <span>{{ formatMoney(row.total_gross, row.currency_code) }}</span>
              </div>
              <div class="caption grey--text text--darken-1">
                {{ row.counterparty || "—" }}<span v-if="row.warehouse"> · {{ row.warehouse }}</span>
              </div>
              <div class="d-flex align-center mt-1">
                <v-chip x-small outlined :color="poStatusColor(row.status)" class="mr-2">{{ statusLabel("PO", row.status) }}</v-chip>
                <span class="caption" :class="row.overdue ? 'error--text font-weight-bold' : 'grey--text'">
                  {{ row.date || $t("DASHBOARD.NO_DATE") }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col v-if="salesOrders" cols="12" lg="4">
          <v-card outlined class="stock-dashboard-list">
            <v-card-title class="subtitle-1 py-3">
              {{ $t("DASHBOARD.NEXT_DELIVERIES") }}
              <v-spacer></v-spacer>
              <v-btn text small color="primary" @click="open('stock-sales-orders')">{{ $t("DASHBOARD.VIEW_ALL") }}</v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <div v-if="!salesOrders.upcoming.length" class="pa-4 caption grey--text">{{ $t("DASHBOARD.EMPTY_LIST") }}</div>
            <div
              v-for="row in salesOrders.upcoming"
              :key="'so-row-' + row.id"
              class="stock-dashboard-row"
              @click="open('stock-sales-orders-view', row.id)"
            >
              <div class="d-flex justify-space-between align-start">
                <strong>{{ row.code }}</strong>
                <span>{{ formatMoney(row.total_gross, row.currency_code) }}</span>
              </div>
              <div class="caption grey--text text--darken-1">
                {{ row.counterparty || "—" }}<span v-if="row.warehouse"> · {{ row.warehouse }}</span>
              </div>
              <div class="d-flex align-center mt-1">
                <v-chip x-small outlined :color="soStatusColor(row.status)" class="mr-2">{{ statusLabel("SO", row.status) }}</v-chip>
                <span class="caption" :class="row.overdue ? 'error--text font-weight-bold' : 'grey--text'">
                  {{ row.date || $t("DASHBOARD.NO_DATE") }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col v-if="deals" cols="12" lg="4">
          <v-card outlined class="stock-dashboard-list">
            <v-card-title class="subtitle-1 py-3">
              {{ $t("DASHBOARD.RECENT_DEALS") }}
              <v-spacer></v-spacer>
              <v-btn text small color="primary" @click="open('deals-list')">{{ $t("DASHBOARD.VIEW_ALL") }}</v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <div v-if="!deals.recent.length" class="pa-4 caption grey--text">{{ $t("DASHBOARD.EMPTY_LIST") }}</div>
            <div
              v-for="row in deals.recent"
              :key="'deal-row-' + row.id"
              class="stock-dashboard-row"
              @click="open('deals-view', row.id)"
            >
              <div class="d-flex justify-space-between align-start">
                <strong>{{ row.name }}</strong>
                <span class="caption grey--text">{{ row.code }}</span>
              </div>
              <div class="caption grey--text text--darken-1 mt-1">
                <span v-if="row.trade_side">{{ tradeSideLabel(row.trade_side) }}</span>
                <span v-if="row.trade_side && row.stage"> · </span>
                <span v-if="row.stage">{{ row.stage }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import { GET_STOCK_DASHBOARD } from "@/store/stock/stockDashboard.module";
import { formatStockMoney, formatStockQuantity } from "@/common/stock/stockNumberFormat";

export default {
  name: "dashboard",
  data() {
    return {
      loading: true,
      loadError: false,
      dashboard: null,
      requestId: 0,
    };
  },
  computed: {
    ...mapGetters(["stockContextVersion"]),
    inventory() {
      return this.dashboard && this.dashboard.inventory;
    },
    purchaseOrders() {
      return this.dashboard && this.dashboard.purchase_orders;
    },
    salesOrders() {
      return this.dashboard && this.dashboard.sales_orders;
    },
    deals() {
      return this.dashboard && this.dashboard.deals;
    },
    transfers() {
      return this.dashboard && this.dashboard.transfers;
    },
    hasContent() {
      return !!(this.inventory || this.purchaseOrders || this.salesOrders || this.deals || this.transfers);
    },
    compositionTotal() {
      if (!this.inventory) {
        return 0;
      }

      return Number(this.inventory.on_hand_free_quantity || 0)
        + Number(this.inventory.reserved_quantity || 0)
        + Number(this.inventory.blocked_quantity || 0);
    },
    inventoryHeadline() {
      if (!this.inventory) {
        return "";
      }

      if (this.inventory.total_inventory_value !== null && this.inventory.total_inventory_value !== undefined) {
        return this.formatMoney(this.inventory.total_inventory_value, this.inventory.currency_code);
      }

      return this.formatQty(this.inventory.quantity_on_hand);
    },
  },
  watch: {
    stockContextVersion() {
      this.load();
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: this.$t("MENU.DASHBOARD") }]);
    this.load();
  },
  methods: {
    load() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.loadError = false;

      this.$store.dispatch(GET_STOCK_DASHBOARD)
        .then((data) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.dashboard = data;
        })
        .catch(() => {
          if (requestId !== this.requestId) {
            return;
          }
          this.loadError = true;
          this.dashboard = null;
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false;
          }
        });
    },
    open(name, id) {
      const params = id ? { id } : {};
      this.$router.push({ name, params }).catch(() => {});
    },
    segmentWidth(value) {
      if (!this.compositionTotal) {
        return "0%";
      }

      return `${(Number(value || 0) / this.compositionTotal) * 100}%`;
    },
    formatQty(value) {
      return formatStockQuantity(value);
    },
    formatMoney(value, currency) {
      return formatStockMoney(value || 0, currency, "");
    },
    moneyLines(totals) {
      return (totals || []).slice(0, 3).map((row) => this.formatMoney(row.total_gross, row.currency_code));
    },
    statusLabel(group, status) {
      const key = `STOCK.STATUSES.${group}.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : (status || "");
    },
    poStatusColor(status) {
      const map = {
        draft: "grey",
        sent: "grey",
        confirmed: "primary",
        partially_received: "orange",
        received: "success",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    soStatusColor(status) {
      const map = {
        draft: "grey",
        confirmed: "primary",
        partially_delivered: "orange",
        delivered: "success",
        invoiced: "teal",
        closed: "grey",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    tradeSideLabel(side) {
      const key = side === "buying" ? "DEALS.TRADE_SIDE.BUYING" : "DEALS.TRADE_SIDE.SELLING";
      return this.$te(key) ? this.$t(key) : side;
    },
  },
};
</script>

<style scoped>
.stock-dashboard-card {
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.stock-dashboard-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.stock-dashboard-card--inventory {
  border-left: 4px solid #00897b !important;
}
.stock-dashboard-card--po {
  border-left: 4px solid #5e35b1 !important;
}
.stock-dashboard-card--so {
  border-left: 4px solid #1565c0 !important;
}
.stock-dashboard-card--deals {
  border-left: 4px solid #ffa000 !important;
}
.stock-dashboard-card--transfers {
  border-left: 4px solid #ef6c00 !important;
}
.stock-dashboard-bar {
  display: flex;
  height: 8px;
  border-radius: 99px;
  overflow: hidden;
  background: #eeeeee;
}
.stock-dashboard-bar__free {
  background: #00897b;
}
.stock-dashboard-bar__reserved {
  background: #fb8c00;
}
.stock-dashboard-bar__blocked {
  background: #90a4ae;
}
.stock-dashboard-legend {
  font-size: 12px;
  margin-right: 12px;
}
.stock-dashboard-legend--free {
  color: #00695c;
}
.stock-dashboard-legend--reserved {
  color: #ef6c00;
}
.stock-dashboard-legend--blocked {
  color: #607d8b;
}
.stock-dashboard-list {
  height: 100%;
}
.stock-dashboard-row {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.stock-dashboard-row:hover {
  background: rgba(0, 0, 0, 0.03);
}
.stock-dashboard-row:last-child {
  border-bottom: 0;
}
</style>
